import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AppShell } from '@/components/layout/app-shell';
import { Inicio, RotaFuncionalidade } from '@/components/layout/rota-funcionalidade';
import { TooltipProvider } from '@/components/ui/tooltip';
import { getAuthPort } from '@/data/auth';
import { dataSource } from '@/data';
import type { Acesso } from '@/data/types';
import { VisibilidadePage } from '@/features/administracao/VisibilidadePage';
import { AcessoContext, type AcessoContexto } from '@/hooks/use-acesso';
import { ACESSO_LIBERADO } from '@/lib/acesso';
import { UsuariosPage } from '@/features/administracao/UsuariosPage';
import { FeedPage } from '@/features/feed/FeedPage';
import { IncidentesPage } from '@/features/incidentes/IncidentesPage';
import { HomologacaoPage } from '@/features/homologacao/HomologacaoPage';
import { PainelPage } from '@/features/painel/PainelPage';
import { RamoFeedPage, RamosPage } from '@/features/ramos/RamosPage';
import { VersaoPage } from '@/features/versao/VersaoPage';
import { SessaoContext } from '@/hooks/use-sessao';

/**
 * Raiz da aplicação: sessão (identidade + capabilities), shell e rotas.
 * As rotas apontam para componentes React reais — uma rota quebrada falha em
 * build, não em produção.
 */
export default function App() {
  const [usuario, setUsuario] = useState<{ nome: string; detalhe: string } | null>(null);
  // `null` = ainda carregando (diferente de `[]`, que é "sem nenhuma capability").
  const [roles, setRoles] = useState<string[] | null>(null);

  useEffect(() => {
    let ativo = true;
    void getAuthPort()
      .getIdentity()
      .then((identidade) => {
        if (ativo) setUsuario(identidade);
      })
      .catch(() => {
        // Identidade é enfeite da topbar: falhar aqui não pode derrubar o app.
        if (ativo) setUsuario(null);
      });
    void getAuthPort()
      .getRoles()
      .then((valor) => {
        if (ativo) setRoles(valor);
      })
      .catch(() => {
        if (ativo) setRoles([]);
      });
    return () => {
      ativo = false;
    };
  }, []);

  // Acesso às funcionalidades: carregado UMA vez aqui. Falha = liberado (a API
  // continua barrando; esconder tudo por um erro de rede seria pior).
  const [acessoDados, setAcessoDados] = useState<{ acesso: Acesso; colaborador: boolean } | null>(null);
  const [tentativaAcesso, setTentativaAcesso] = useState(0);
  useEffect(() => {
    let ativo = true;
    dataSource
      .me()
      .then((me) => {
        if (ativo) setAcessoDados({ acesso: me.acesso, colaborador: me.colaborador });
      })
      .catch(() => {
        if (ativo) setAcessoDados((atual) => atual ?? { acesso: ACESSO_LIBERADO, colaborador: true });
      });
    return () => {
      ativo = false;
    };
  }, [tentativaAcesso]);
  const recarregarAcesso = useCallback(() => setTentativaAcesso((valor) => valor + 1), []);
  const acessoCtx = useMemo<AcessoContexto>(
    () => ({
      carregando: acessoDados === null,
      colaborador: acessoDados?.colaborador ?? true,
      acesso: acessoDados?.acesso ?? ACESSO_LIBERADO,
      recarregar: recarregarAcesso,
    }),
    [acessoDados, recarregarAcesso],
  );

  const sessao = useMemo(
    () => ({ usuario, roles: roles ?? [], rolesCarregando: roles === null }),
    [usuario, roles],
  );

  return (
    <SessaoContext.Provider value={sessao}>
      <AcessoContext.Provider value={acessoCtx}>
      <TooltipProvider>
        <AppShell usuario={usuario} roles={sessao.roles}>
          <Routes>
            <Route path="/" element={<Inicio />} />
            <Route path="/feed" element={<RotaFuncionalidade funcionalidade="feed"><FeedPage /></RotaFuncionalidade>} />
            <Route path="/ramos" element={<RotaFuncionalidade funcionalidade="ramos"><RamosPage /></RotaFuncionalidade>} />
            <Route path="/ramos/:ramo" element={<RotaFuncionalidade funcionalidade="ramos"><RamoFeedPage /></RotaFuncionalidade>} />
            <Route path="/incidentes" element={<RotaFuncionalidade funcionalidade="incidentes"><IncidentesPage /></RotaFuncionalidade>} />
            <Route path="/incidentes/:id" element={<RotaFuncionalidade funcionalidade="incidentes"><IncidentesPage /></RotaFuncionalidade>} />
            <Route path="/versoes/:versao" element={<VersaoPage />} />
            <Route path="/homologacao" element={<HomologacaoPage />} />
            <Route path="/painel" element={<RotaFuncionalidade funcionalidade="painel"><PainelPage /></RotaFuncionalidade>} />
            <Route path="/usuarios" element={<UsuariosPage />} />
            <Route path="/visibilidade" element={<VisibilidadePage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AppShell>

        {/* Toast pontual: confirma ação discreta; erro que exige decisão vira
            estado inline na própria tela, não um toast que some sozinho. */}
        <Toaster position="top-right" richColors closeButton toastOptions={{ duration: 5000 }} />
      </TooltipProvider>
      </AcessoContext.Provider>
    </SessaoContext.Provider>
  );
}
